import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/language";
import {
  Search,
  HandshakeIcon,
  Scale,
  Hammer,
  Building2,
  FileText,
  CheckCircle2,
  UserCircle,
} from "lucide-react";
import { useState, useEffect } from "react";

// Import all gallery images using Vite's import.meta.glob
const galleryModules = import.meta.glob("/src/assets/gallery/*.jpg", {
  eager: true,
});

// Extract the default exports (image URLs) from the modules
const galleryImages = Object.values(galleryModules)
  .map((module: any) => module.default)
  .filter(Boolean); // Filter out any undefined values

export function AboutSection() {
  const { t, language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-switch images every 4 seconds
  useEffect(() => {
    if (galleryImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
      }, 4000);

      return () => clearInterval(interval);
    }
  }, []);

  const features = [
    {
      icon: Search,
      title: {
        en: "Personalized Property Search",
        he: "חיפוש נכסים מותאם אישית",
        fr: "Recherche Immobilière Personnalisée",
      },
      description: {
        en: "Homes that match your lifestyle, needs, and budget.",
        he: "בתים שמתאימים לסגנון החיים, הצרכים והתקציב שלך.",
        fr: "Des maisons qui correspondent à votre style de vie, vos besoins et votre budget.",
      },
    },
    {
      icon: HandshakeIcon,
      title: {
        en: "Smart Negotiation",
        he: "משא ומתן חכם",
        fr: "Négociation Intelligente",
      },
      description: {
        en: "Professional representation to secure the best price and terms.",
        he: "ייצוג מקצועי להבטחת המחיר והתנאים הטובים ביותר.",
        fr: "Représentation professionnelle pour obtenir le meilleur prix et les meilleures conditions.",
      },
    },
    {
      icon: Scale,
      title: {
        en: "Legal Support",
        he: "תמיכה משפטית",
        fr: "Support Juridique",
      },
      description: {
        en: "Access to trusted real-estate attorneys who specialize in smooth, safe transactions.",
        he: "גישה לעורכי דין מקרקעין מהימנים המתמחים בעסקאות חלקות ובטוחות.",
        fr: "Accès à des avocats immobiliers de confiance spécialisés dans les transactions fluides et sécurisées.",
      },
    },
    {
      icon: Hammer,
      title: {
        en: "Renovation & Upgrades",
        he: "שיפוצים ושדרוגים",
        fr: "Rénovation et Améliorations",
      },
      description: {
        en: "Reliable contractors for remodeling, design, and home improvements.",
        he: "קבלנים אמינים לשיפוץ, עיצוב ושיפורי בית.",
        fr: "Entrepreneurs fiables pour la rénovation, la conception et les améliorations domiciliaires.",
      },
    },
    {
      icon: Building2,
      title: {
        en: "Bank Account Assistance",
        he: "סיוע בפתיחת חשבון בנק",
        fr: "Assistance Bancaire",
      },
      description: {
        en: "Step-by-step help opening an Israeli bank account and understanding all requirements.",
        he: "עזרה שלב אחר שלב בפתיחת חשבון בנק ישראלי והבנת כל הדרישות.",
        fr: "Aide étape par étape pour ouvrir un compte bancaire israélien et comprendre toutes les exigences.",
      },
    },
    {
      icon: FileText,
      title: {
        en: "Complete Aliyah Support",
        he: "תמיכה מלאה בעלייה",
        fr: "Support Aliyah Complet",
      },
      description: {
        en: "Paperwork, bureaucracy, and everything you need to settle in with confidence.",
        he: "ניירת, בירוקרטיה וכל מה שאתה צריך כדי להשתקע בבטחון.",
        fr: "Paperasse, bureaucratie et tout ce dont vous avez besoin pour vous installer en toute confiance.",
      },
    },
  ];

  const stats = [
    {
      number: "100+",
      label: {
        en: "Families Helped",
        he: "משפחות שנעזרו",
        fr: "Familles Aidées",
      },
    },
    {
      number: "100%",
      label: {
        en: "Client Satisfaction",
        he: "שביעות רצון לקוחות",
        fr: "Satisfaction Client",
      },
    },
    {
      number: "12",
      label: {
        en: "Cities Served",
        he: "ערים בשירות",
        fr: "Villes Servies",
      },
    },
  ];

  return (
    <section
      id="about"
      className="py-12 md:py-20 bg-gradient-to-b from-background to-muted/30"
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t("about")}
          </h2>
          <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
            {t("aboutDescription")}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-12 md:mb-16">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="group relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* Top accent bar */}
                <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-primary/30 via-primary to-primary/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Faded number watermark */}
                <span
                  className="absolute top-3 right-4 text-6xl font-black leading-none select-none text-primary/[0.05] group-hover:text-primary/[0.09] transition-colors duration-300"
                  aria-hidden="true"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>

                <CardHeader className="pb-2 pt-6 px-6 items-center text-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/10 group-hover:scale-110 group-hover:from-primary/25 group-hover:to-primary/10 transition-all duration-300 shadow-sm">
                    <Icon size={24} className="text-primary" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-base md:text-lg font-bold text-foreground leading-snug px-2">
                    {feature.title[language]}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-6 pb-6 text-center">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description[language]}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main About Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 md:mb-16">
          <div className="order-2 lg:order-1">
            <h3 className="text-xl md:text-3xl font-bold text-foreground mb-4 md:mb-6">
              {t("whyChooseUs")}
            </h3>
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                  <CheckCircle2 size={12} className="text-primary md:w-3.5 md:h-3.5" />
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("whyChoosePoint1")}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                  <CheckCircle2 size={12} className="text-primary md:w-3.5 md:h-3.5" />
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("whyChoosePoint2")}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                  <CheckCircle2 size={12} className="text-primary md:w-3.5 md:h-3.5" />
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("whyChoosePoint3")}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                  <CheckCircle2 size={12} className="text-primary md:w-3.5 md:h-3.5" />
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("whyChoosePoint4")}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                  <CheckCircle2 size={12} className="text-primary md:w-3.5 md:h-3.5" />
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("whyChoosePoint5")}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">
                  <CheckCircle2 size={12} className="text-primary md:w-3.5 md:h-3.5" />
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  {t("whyChoosePoint6")}
                </p>
              </div>
            </div>
            <div className="mt-4 md:mt-6">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-accent hover:bg-accent/90"
                onClick={() =>
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                {t("getInTouch")}
              </Button>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <div className="relative">
              {/* Gallery Container */}
              <div className="relative w-full h-64 md:h-96 rounded-2xl overflow-hidden shadow-2xl">
                {galleryImages.length > 0 ? (
                  <>
                    {/* Current Image */}
                    <img
                      src={galleryImages[currentImageIndex]}
                      alt={`Gallery image ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover transition-opacity duration-500"
                      onError={(e) => {
                        // Skip to next image if current one fails
                        setCurrentImageIndex(
                          (prev) => (prev + 1) % galleryImages.length
                        );
                      }}
                    />

                    {/* Image Indicators */}
                    {galleryImages.length > 1 && (
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 hidden md:flex space-x-2">
                        {galleryImages.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                              index === currentImageIndex
                                ? "bg-white scale-125"
                                : "bg-white/50 hover:bg-white/75"
                            }`}
                            aria-label={`Go to image ${index + 1}`}
                          />
                        ))}
                      </div>
                    )}

                    {/* Navigation Arrows */}
                    {galleryImages.length > 1 && (
                      <>
                        <button
                          onClick={() =>
                            setCurrentImageIndex((prev) =>
                              prev === 0 ? galleryImages.length - 1 : prev - 1
                            )
                          }
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white p-2 rounded-full transition-all duration-300"
                          aria-label="Previous image"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 19l-7-7 7-7"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() =>
                            setCurrentImageIndex(
                              (prev) => (prev + 1) % galleryImages.length
                            )
                          }
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white p-2 rounded-full transition-all duration-300"
                          aria-label="Next image"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  /* Fallback if no images are found */
                  <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <UserCircle
                          size={24}
                          className="text-primary md:w-8 md:h-8"
                        />
                      </div>
                      <p className="text-base md:text-lg font-semibold text-foreground">
                        Samantha Azoulay
                      </p>
                      <p className="text-sm md:text-base text-muted-foreground px-4">
                        {t("agentTitle")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl md:text-4xl font-bold text-primary mb-1 md:mb-2">
                {stat.number}
              </div>
              <div className="text-sm md:text-base text-muted-foreground">
                {stat.label[language]}
              </div>
            </div>
          ))}
        </div> */}
      </div>
    </section>
  );
}
